/**
 * Per-Page Theme Configuration for www-sbi
 *
 * This file defines which theme should be applied to each page/route.
 * The theme is set via data-theme attribute on the <html> element.
 *
 * Future: Will support section-level theming via data-theme-section attribute
 */

import type { ThemeId } from '@smallbiz/brand-kit/themes';

/**
 * Page theme configuration
 *
 * Maps route patterns to theme IDs.
 * Routes are matched using string matching or regex.
 */
export interface PageThemeConfig {
  /** Route pattern to match (supports * wildcard) */
  route: string;
  /** Theme ID to apply */
  theme: ThemeId;
  /** Optional description of why this theme is used */
  description?: string;
}

/**
 * Default theme for pages not explicitly configured.
 * Default Canvas is the canonical SBI primary theme. theme.css styles it and the legacy id
 * "canvas" that astro-sbi still sets (#3071 S1)
 * default so that --primary resolves to #1167f1 (Blue 400) across both app types.
 */
export const DEFAULT_THEME: ThemeId = 'default-canvas';

/**
 * Per-page theme mappings
 *
 * Add new pages here to set their theme.
 * Pages are matched in order, first match wins.
 */
export const PAGE_THEMES: PageThemeConfig[] = [
  // Home page - Canvas theme (canonical SBI primary, matches astro-sbi default so --primary is consistent)
  {
    route: '/',
    theme: 'default-canvas',
    description: 'Homepage uses Default Canvas theme — ensures --primary (#1167f1 Blue 400) matches astro-sbi canvas pages (R-55)',
  },

  // Sales Hub - Blue theme (momentum and action)
  {
    route: '/sales/*',
    theme: 'blue-momentum',
    description: 'Sales pages use Blue Momentum theme for energy and conversion',
  },

  // Marketing Hub - Sunshine theme (optimistic and engaging)
  {
    route: '/marketing/*',
    theme: 'sunshine-energy',
    description: 'Marketing pages use Sunshine Lift theme for positivity and engagement',
  },

  // Finance Hub - Emerald theme (growth and sustainability)
  {
    route: '/finance/*',
    theme: 'emerald-momentum',
    description: 'Finance pages use Emerald Focus theme for financial wellbeing',
  },

  // Automation Hub - Canvas theme (clean and technical)
  {
    route: '/automation/*',
    theme: 'default-canvas',
    description: 'Automation pages use Default Canvas theme for technical clarity',
  },

  // Premium features - Magenta theme (premium and exclusive)
  {
    route: '/premium',
    theme: 'magenta-premium',
    description: 'Premium tier uses Magenta Premium theme for exclusivity',
  },

  // About, Company, Team pages - Storm theme
  {
    route: '/about',
    theme: 'storm-confidence',
    description: 'Company pages use Storm Confidence theme',
  },
  {
    route: '/team/*',
    theme: 'storm-confidence',
    description: 'Team pages use Storm Confidence theme',
  },

  // Brand pages - Canvas theme (default)
  {
    route: '/brand/*',
    theme: 'default-canvas',
    description: 'Brand guidelines use clean Canvas theme',
  },

  // Glossary - Canvas theme
  {
    route: '/glossary',
    theme: 'default-canvas',
    description: 'Reference pages use clean Canvas theme',
  },
];

/**
 * Match a route pattern against a pathname
 *
 * Supports wildcards:
 * - /sales/* matches /sales, /sales/hub, /sales/hub/overview
 * - /sales matches only /sales (exact match)
 */
export function matchRoute(pattern: string, pathname: string): boolean {
  // Normalize paths (remove trailing slashes except for root)
  const normalizePath = (path: string) => {
    if (path === '/') return path;
    return path.replace(/\/$/, '');
  };

  const normalizedPattern = normalizePath(pattern);
  const normalizedPath = normalizePath(pathname);

  // Exact match
  if (normalizedPattern === normalizedPath) {
    return true;
  }

  // Wildcard match (e.g., /sales/* matches /sales/hub)
  if (normalizedPattern.endsWith('/*')) {
    const prefix = normalizedPattern.slice(0, -2);
    return (
      normalizedPath === prefix || // /sales/* matches /sales
      normalizedPath.startsWith(prefix + '/') // /sales/* matches /sales/hub
    );
  }

  return false;
}

/**
 * Get the theme for a given pathname
 *
 * Returns the first matching theme from PAGE_THEMES,
 * or DEFAULT_THEME if no match found.
 */
export function getThemeForPage(pathname: string): ThemeId {
  for (const config of PAGE_THEMES) {
    if (matchRoute(config.route, pathname)) {
      return config.theme;
    }
  }

  return DEFAULT_THEME;
}

/**
 * Section theme configuration (for future use)
 *
 * Allows setting different themes for sections within a page.
 * Use data-theme-section attribute on section elements.
 */
export interface SectionThemeConfig {
  /** Section ID or name */
  section: string;
  /** Theme ID to apply */
  theme: ThemeId;
  /** Optional description */
  description?: string;
}

/**
 * Apply section theme to an element
 *
 * Sets data-theme-section attribute on the element.
 * CSS should be extended to support [data-theme-section="theme-id"] selectors.
 *
 * Example usage:
 *   <section ref={(el) => el && applySectionTheme(el, 'magenta')}>
 *     Content with magenta theme
 *   </section>
 */
export function applySectionTheme(element: HTMLElement, theme: ThemeId): void {
  element.setAttribute('data-theme-section', theme);
}

/**
 * Remove section theme from an element
 */
export function removeSectionTheme(element: HTMLElement): void {
  element.removeAttribute('data-theme-section');
}
